import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Skeleton, Typography, useTheme } from "@mui/material";
import { faBan } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { BaseCard } from "@utilifeed/components";
import { Sensor } from "@core/apis/shared/types/sensors.model";

import SensorGrid from "./SensorGrid";
import type { Building } from "./useSensorOverviewData";

type SensorSelectionPanelProps = Readonly<{
  buildings: Building[];
  allFloors: string[];
  activeSensors: number;
  totalSensors: number;
  fullSensors: Sensor[];
  selectedSensors?: string[];
  setSelectedSensors: (uids: string[]) => void;
  onSensorSelect?: (sensorId: string) => void;
  targetTemperature?: number | null;
  isLoading?: boolean;
}>;

const SensorSelectionPanel = ({
  buildings,
  allFloors,
  activeSensors,
  totalSensors,
  fullSensors,
  selectedSensors,
  setSelectedSensors,
  onSensorSelect,
  targetTemperature,
  isLoading,
}: SensorSelectionPanelProps) => {
  const { t } = useTranslation(["propai_properties", "_action"]);
  const theme = useTheme();

  const handleSelectAll = useCallback(() => {
    const allUids = fullSensors.map((sensor) => sensor.uid);
    if (
      selectedSensors?.length !== allUids.length ||
      !selectedSensors.every((uid) => allUids.includes(uid))
    ) {
      setSelectedSensors(allUids);
    }
  }, [fullSensors, selectedSensors, setSelectedSensors]);

  const handleDeselectAll = () => {
    setSelectedSensors([]);
  };

  return (
    <BaseCard
      title={
        <>
          <Typography variant="titleSmall" component="span" pr={2}>
            {t("climate.individual_sensors_title")}{" "}
          </Typography>
          <Chip
            label={
              <Typography variant="labelLarge">
                {t("climate.active_sensors", { activeSensors, totalSensors })}
              </Typography>
            }
          />
        </>
      }
      titleAddon={
        <Box display="flex" gap={2} p={1} alignItems="center">
          <Typography variant="labelLarge" color="text.secondary">
            {t("climate.select_sensors_to_view_historic_data")}
          </Typography>
          <Button
            variant="outlined"
            sx={{ borderRadius: "20px", textTransform: "none" }}
            onClick={handleSelectAll}
            disabled={!!(selectedSensors?.length === fullSensors.length)}
          >
            {t("_action:action_select_all")}
          </Button>
          <Button
            variant="outlined"
            sx={{ borderRadius: "20px", textTransform: "none" }}
            disabled={!selectedSensors?.length}
            onClick={handleDeselectAll}
          >
            {t("_action:action_deselect_all")}
          </Button>
        </Box>
      }
      isCardRounded
    >
      <Box p={2}>
        {isLoading ? (
          <Skeleton variant="rectangular" height={100} />
        ) : (
          <SensorGrid
            buildings={buildings}
            allFloors={allFloors}
            selectedSensors={selectedSensors}
            onSensorSelect={onSensorSelect}
            targetTemperature={targetTemperature}
          />
        )}
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          px: 2,
          pb: 2,
        }}
      >
        <FontAwesomeIcon
          icon={faBan}
          style={{ fontSize: "14px", color: theme.palette.text.secondary }}
        />

        <Typography variant="bodyMedium" color="text.secondary">
          {t("propai_properties:excluded_sensor")}
        </Typography>
      </Box>
    </BaseCard>
  );
};

export default SensorSelectionPanel;
